import type { JsonValue, PageNode } from "@tento-ui/brand/page-model";

// Reading a node's untyped prop bag, with the two rules this codebase keeps
// getting wrong when it reads them inline: a missing prop is `null` rather
// than `""`, and a prop of the wrong TYPE is also `null` rather than a
// stringified surprise. A section that renders `[object Object]` because a
// model carried an array where it promised a string is a content bug wearing
// the costume of a rendering bug.
export function stringProp(node: PageNode, key: string): Option<string> {
  const value = node.props[key];
  return typeof value === "string" ? value : null;
}

export function arrayProp(node: PageNode, key: string): JsonValue[] {
  const value = node.props[key];
  return Array.isArray(value) ? value : [];
}

export function stringsProp(node: PageNode, key: string): string[] {
  return arrayProp(node, key).filter((v): v is string => typeof v === "string");
}

/**
 * Set an element's text, or hide it when the model did not supply any.
 *
 * Every optional line in these sections needs this pair, and doing it inline
 * gets it wrong in one direction or the other: text without the hide leaves an
 * empty element still claiming its margins, and the hide without the text
 * leaves yesterday's content behind when a node re-binds.
 */
export function setTextOrHide($el: HTMLElement, text: Option<string>): void {
  $el.textContent = text ?? "";
  $el.toggleAttribute("hidden", !text);
}

/** A JSON object entry from a list prop, or null for anything else. */
export function objectEntry(value: JsonValue): Option<Record<string, JsonValue>> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  return value as Record<string, JsonValue>;
}

/** A string field off one of those entries, with the same wrong-type rule. */
export function stringField(entry: Record<string, JsonValue>, key: string): Option<string> {
  const value = entry[key];
  return typeof value === "string" ? value : null;
}
